import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyTheme, readStoredTheme, resolveInitialTheme, toggleTheme, watchSystemTheme, type Theme } from "../theme";

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M20 14.5A8.5 8.5 0 0 1 9.5 4 7 7 0 1 0 20 14.5Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme() ?? resolveInitialTheme());

  useEffect(() => {
    applyTheme(theme);
    return watchSystemTheme(setTheme);
  }, [theme]);

  const isDark = theme === "dark";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={t("a11y.theme")}
      className="bx-focus relative inline-flex h-9 w-[4.25rem] shrink-0 items-center rounded-full border transition"
      style={{
        backgroundColor: "var(--color-toggle-track)",
        borderColor: "var(--color-border)",
      }}
      onClick={() => setTheme((c) => toggleTheme(c))}
    >
      <span
        className="absolute left-1 flex h-7 w-7 items-center justify-center rounded-full shadow-sm transition-transform duration-200"
        style={{
          backgroundColor: "var(--color-toggle-thumb)",
          color: "var(--color-primary)",
          transform: isDark ? "translateX(2rem)" : "translateX(0)",
        }}
      >
        {isDark ? <MoonIcon /> : <SunIcon />}
      </span>
      <span className="sr-only">{isDark ? "Dark" : "Light"}</span>
    </button>
  );
}
